/**
 * One table of list rows, which OwnersTable, OfficersTable and SubsidiariesTable draw: one row per
 * owner, officer, subsidiary or state registration, one cell per column they name, and an
 * unlabelled Approval column.
 *
 * Expects the rows the api sent, the columns to draw (the first one is the row's name), the key of
 * the name cell and the noun of a row, the tax year the page shows, the "Needs review only" filter,
 * the page's writes (useProfileFieldWrites), busy while a write is in flight, the callback that
 * opens a source document, and optionally `renderFooter`, which draws rows under the body with the
 * choices made so far (the owners' Total row).
 *
 * Every cell is a button that opens ValuePicker under it, whatever its state, so a person changes an
 * approved value there too. What a choice made in that panel does depends on the row:
 *  - A row that waits for a person (rowIsWaiting: its name is not approved yet, or a cell has a
 *    candidate waiting) keeps the choice on the page and writes nothing. Its Approval cell holds a
 *    yellow Finish review button. In the subsidiaries and state registrations tables, clicking it
 *    shows Cancel and a green Confirm review, and Confirm review sends every choice of the row at
 *    once (rowReviewWrites in listRows.ts, reviewListRow in useProfileFieldWrites.tsx); the cells
 *    the api refused are listed in the Approval cell, one line each, "<column>: <the api's
 *    sentence>". In the owners and officers tables it opens the person window described below.
 *  - A row with nothing waiting writes a typed value at once through the edit route, as a value of
 *    the profile's sections does, and its Approval cell shows the "Saved" notice with its Undo for
 *    as long as the page keeps it; otherwise the green check and "Reviewed by <person> on <date>".
 *
 * A row that waits has a yellow left edge; its name is followed by "New owner, found in
 * <document>" when nobody has approved the name. A cell that waits has a dashed yellow border and a
 * yellow background, shows the value Confirm review would write (rowCellValue: the choice, else the
 * newest waiting value), in italics when nothing is approved for it, and carries a small tag:
 * "Changed" where the value differs from the approved one, "Kept" once a person chose the approved
 * value, "Edited" once a person typed one.
 *
 * With `personDetails` (the owners and officers tables), the name is a button styled as a link that
 * opens a window (GenericModal) instead of ValuePicker, and the window is where the row is
 * reviewed. The window holds every cell of the row in two lists: "In the table", the cells of the
 * columns the table draws, the name first; and "Details", every cell whose column the table does
 * not draw (detailKeys: the taxpayer number, owner_tin or officer_ssn, which the api sends masked to
 * its last four digits, then an owner's address, capital share and beginning capital account). Each
 * cell is drawn by the same ListCell as in the row, with the same
 * ValuePicker, the same waiting colors and tags, and the same choices, which ListTable keeps by row
 * for both. A per-year detail column's label carries the tax year the page shows ("Capital share
 * (2024)"). On a row with nothing waiting, a value typed into a window cell is written at once, the
 * "Saved" notice with its Undo is drawn under that cell in the window as well as in the row's
 * Approval cell, and the window's only button is Close. On a row that waits, the row's Finish
 * review button opens the window, and the window's own Finish review shows Cancel and a green
 * Confirm review, which sends every choice of the row (rowReviewWrites goes over every cell of the
 * row); the api's sentence for each refused cell is drawn under that cell, and the window closes
 * when nothing was refused. The row itself has no Confirm review. At the foot of the window, on the
 * left, a red Remove owner / Remove officer button (removePersonButton) asks in place whether to
 * remove the person with every value on file for them (removePersonQuestion), with Cancel and a red
 * Remove; Remove sends removeListRow (useProfileFieldWrites.tsx), and the window closes once the
 * api has removed the row, or shows the api's sentence under the buttons when it refused. When
 * cells only the window shows wait and have no choice yet, the row says so under the name ("2 more
 * values to review"), and that line opens the window too. When the window's last Confirm review
 * had cells the api refused and the window has been closed, the row shows a red line under the
 * name, PERSON_DETAILS_WORDS.refused, which opens the window as well.
 *
 * Every row carries an id (listRowElementId) that "Go to field" on a document's summary screen
 * scrolls to, and the row that names highlightedElementId carries HIGHLIGHT_ROW_CLASS.
 *
 * From Tailwind's lg width (1024px) up the table is an ordinary fixed table whose header widths
 * decide the columns. Below it the header is hidden and each row wraps (LIST_TABLE_LAYOUT): the
 * name on a line of its own, the other cells two or three to a line, each with its column's header
 * above the value (drawn from the cell's data-label by a CSS ::before, so it adds no text to the
 * page), and the Approval cell on a last line, so no column is squeezed to a few pixels.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TAX_PROFILE_LIST_COLUMNS } from '@shared/constants/taxProfileFields';
import type {
  TaxProfileFieldValue,
  TaxProfileListRow,
  TaxProfilePersonTable,
} from '@shared/types/taxProfile';
import { GenericModal } from '@/components/Modal';
import { formatFieldValue } from '@/pages/TaxProfile/format';
import { fieldNeedsReview, newestWaiting } from '@/pages/TaxProfile/fieldState';
import {
  KeptRowDecisions,
  RowDecisions,
  decisionKey,
  decisionsForYear,
  listCellTarget,
  listColumn,
  rowApproval,
  rowCellValue,
  rowIsWaiting,
  rowReviewWrites,
} from '@/pages/TaxProfile/listRows';
import {
  ListRowNoun,
  EMPTY_CELL_DASH,
  LIST_EMPTY_SENTENCES,
  PERSON_DETAILS_WORDS,
  REMOVE_PERSON_WORDS,
  ROW_PICKER_NOTE,
  ROW_WORDS,
  labelForYear,
  moreToReviewLine,
  newRowLine,
  personDetailsHeading,
  removePersonButton,
  removePersonQuestion,
  reviewedSentence,
} from '@/pages/TaxProfile/wording';
import {
  ProfileFieldWrites,
  RowWriteFailure,
  noticeKey,
} from '@/pages/TaxProfile/useProfileFieldWrites';
import {
  FieldRowNotice,
  HIGHLIGHT_ROW_CLASS,
  NoticeText,
  VALUE_TEXT_CLASS,
} from '@/pages/TaxProfile/components/FieldRow';
import SourceText from '@/pages/TaxProfile/components/SourceText';
import ValuePicker from '@/pages/TaxProfile/components/ValuePicker';
import type {
  FieldDecision,
  PickerDefinition,
} from '@/pages/TaxProfile/components/ValuePicker';

/** What a cell's button is drawn from: the value it shows, the cell, and the choice made for it. */
export type ListCellContext = {
  value: string | number | boolean | null;
  cell: TaxProfileFieldValue | undefined;
  decision: FieldDecision | undefined;
};

export type ListTableColumn = {
  /** The key of the list column (TAX_PROFILE_LIST_COLUMNS) the cell holds. */
  key: string;
  header: string;
  align?: 'right';
  /** The width class of the column's header; the name column takes what is left. */
  widthClass?: string;
  /**
   * The text of the cell, which is also its button's accessible name after the header. By default
   * the value in the shape its column type calls for, and a dash for an empty cell.
   */
  text?: (context: ListCellContext) => string;
  /** What the button draws instead of that text, as the US person column's icon. */
  render?: (context: ListCellContext) => ReactNode;
};

export interface ListTableProps {
  rows: TaxProfileListRow[];
  /** The columns to draw, the row's name first. */
  columns: ListTableColumn[];
  /** The accessible name of the table. */
  label: string;
  nameKey: string;
  noun: ListRowNoun;
  taxYear: number | null;
  needsReviewOnly: boolean;
  /**
   * The page's writes. removeListRow is what the person window's Remove owner / Remove officer
   * button sends; without it the window has no such button.
   */
  writes: Pick<ProfileFieldWrites, 'notices' | 'editValue' | 'reviewListRow'> &
    Partial<Pick<ProfileFieldWrites, 'removeListRow'>>;
  busy?: boolean;
  onOpenSourceDocument?: (documentId: number) => void;
  /**
   * True for the owners and officers tables: the name opens the window with the cells the table
   * does not draw, the taxpayer number among them, described at the top of this file.
   */
  personDetails?: boolean;
  /**
   * The element id "Go to field" just sent the page to (listRowElementId); the row it names
   * carries HIGHLIGHT_ROW_CLASS. Null when there is none.
   */
  highlightedElementId?: string | null;
  /** Rows drawn under the body, given the choices made in each row so far. */
  renderFooter?: (
    decisionsOf: (row: TaxProfileListRow) => RowDecisions
  ) => ReactNode;
  /**
   * Called with the table's noun and how many rows have choices Confirm review has not saved yet,
   * each time that number changes, and with 0 when the table goes away. TaxProfile.tsx adds them up
   * to warn before a year or entity switch, or leaving the page, loses or leaves them behind.
   */
  onUnsavedChoicesChange?: (noun: ListRowNoun, count: number) => void;
}

/**
 * The id of one list row, which "Go to field" on a document's summary screen scrolls to. The noun
 * is part of it because the four tables number their rows separately: 'tax-profile-owner-30',
 * 'tax-profile-state-registration-60'.
 */
export const listRowElementId = (noun: ListRowNoun, recordId: number) =>
  `tax-profile-${noun.replace(/ /g, '-')}-${recordId}`;

/**
 * The table a row of the owners or the officers table is removed from, by the row's noun; the
 * subsidiaries and state registrations tables have no person window and no removal.
 */
const PERSON_TABLES: Partial<Record<ListRowNoun, TaxProfilePersonTable>> = {
  owner: 'k1_partners',
  officer: 'entity_officers',
};

/** The text of a cell by default: its value in its column's or its field's shape, or a dash. */
export const defaultText =
  (definition: PickerDefinition) =>
  ({ value }: ListCellContext): string =>
    value === null || value === undefined || value === ''
      ? EMPTY_CELL_DASH
      : formatFieldValue(value, definition);

/** The small tag of a cell that waits, or of a cell a person typed into; null for the others. */
const cellTag = (
  cell: TaxProfileFieldValue | undefined,
  decision: FieldDecision | undefined
): string | null => {
  if (decision?.kind === 'type') return ROW_WORDS.edited;
  if (cell?.status !== 'changed') return null;
  return decision?.kind === 'keep' ? ROW_WORDS.kept : ROW_WORDS.changed;
};

/**
 * A cell's button. It wraps (flex-wrap), so the tag and the chevron move under the value in a
 * narrow column instead of taking width from it.
 */
const CELL_BUTTON_CLASS =
  'inline-flex flex-wrap max-w-full items-center gap-x-2 gap-y-1 text-left rounded px-2 py-1 -mx-2 border ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary';

/**
 * The classes of the table, its parts and its cells for the two layouts: below lg every row wraps
 * as described at the top of this file, from lg up the table is an ordinary fixed table.
 * OwnersTable's Total row uses them too.
 *
 * The table turns back into a table from lg up with lg:[display:table], not lg:table: in this
 * project `table` is also daisyUI's table class, so lg:table repeats every daisyUI table rule inside
 * the lg media query, after Tailwind's utilities, and its `white-space: nowrap` on every cell beat
 * the cells' whitespace-normal. Labels then ran into the next column and the Approval sentence ran
 * past the table's right edge.
 */
export const LIST_TABLE_LAYOUT = {
  /**
   * The cells' z-index is reset to auto, with !important because ClientList's TableClient.css gives
   * every daisyUI table cell `z-index: 1 !important`. That makes each flex cell below lg its own
   * stacking context, so ValuePicker's popup drew under the cells after it.
   */
  table:
    'table table-compact w-full block lg:[display:table] lg:table-fixed [&_td]:!z-auto [&_th]:!z-auto',
  head: 'hidden lg:table-header-group',
  body: 'block lg:table-row-group',
  foot: 'block lg:table-footer-group',
  row: 'flex flex-wrap lg:table-row border-l-4 lg:border-l-0',
  /**
   * The name cell: a line of its own below lg; its left edge from lg up, the row's below. Below lg
   * only the Approval cell, the last line of a row, keeps the table's line under it.
   */
  nameCell:
    'w-full lg:w-auto lg:border-l-4 border-b-0 lg:border-b lg:border-b-base-200',
  /**
   * Any other cell: two to a line, three from sm, with its header above it below lg. A cell left
   * alone on the last line grows to fill it, so the row's background does not show beside it.
   */
  cell:
    'w-1/2 sm:w-1/3 grow lg:w-auto border-b-0 lg:border-b lg:border-b-base-200 ' +
    'before:block before:content-[attr(data-label)] before:text-xs before:font-bold ' +
    'before:uppercase before:opacity-60 before:mb-1 lg:before:hidden',
  /** A cell with nothing in it, as in the Total row, which takes no line below lg. */
  emptyCell: 'hidden lg:table-cell',
  approvalCell: 'w-full lg:w-auto text-right lg:text-center',
  header: 'whitespace-normal',
} as const;

/**
 * The red line under the name once the person window's Confirm review had refusals and the window
 * was closed; it opens the window. Nothing while `show` is false.
 */
function RefusedLine({ show, onOpen }: { show: boolean; onOpen: () => void }) {
  if (!show) return null;
  return (
    <button
      type='button'
      className='block link link-hover text-xs text-error mt-1 text-left'
      onClick={onOpen}
    >
      {PERSON_DETAILS_WORDS.refused}
    </button>
  );
}

/**
 * The line under the name when `count` cells only the person window shows wait for a person; it
 * opens the window. Nothing when `count` is 0.
 */
function DetailsWaitingLine({
  count,
  onOpen,
}: {
  count: number;
  onOpen: () => void;
}) {
  if (count === 0) return null;
  return (
    <button
      type='button'
      className='block link link-hover text-xs text-warning mt-1 text-left'
      onClick={onOpen}
    >
      {moreToReviewLine(count)}
    </button>
  );
}

export interface ListCellProps {
  column: ListTableColumn;
  /**
   * The list column or the catalog field the cell holds; the list column `column.key` names when
   * left out. A document's summary screen (ReviewModal) passes a catalog field here.
   */
  definition?: PickerDefinition;
  cell: TaxProfileFieldValue | undefined;
  decision: FieldDecision | undefined;
  /** True when the row waits for a person, so a choice is kept rather than written. */
  rowWaiting: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDecide: (decision: FieldDecision) => void;
  busy: boolean;
  onOpenSourceDocument?: (documentId: number) => void;
  bold?: boolean;
  /**
   * What a click on the cell does instead of opening ValuePicker: the name opening the person
   * window. With it the cell is drawn as a link rather than as a picker's button.
   */
  onActivate?: () => void;
  /** The button's data-testid. */
  testId: string;
  /** The line under ValuePicker's first sentence on a waiting row; ROW_PICKER_NOTE when left out. */
  note?: string;
  /** The document whose options ValuePicker tags "This document" (ValuePicker's thisDocumentId). */
  thisDocumentId?: number | null;
}

/**
 * One value's button and the ValuePicker it opens: a cell of a list row in the table and the
 * person window, and a waiting line on a document's summary screen (ReviewModal).
 */
export function ListCell({
  column,
  definition: givenDefinition,
  cell,
  decision,
  rowWaiting,
  isOpen,
  onOpenChange,
  onDecide,
  busy,
  onOpenSourceDocument,
  bold = false,
  onActivate,
  testId,
  note = ROW_PICKER_NOTE,
  thisDocumentId = null,
}: ListCellProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const definition = givenDefinition ?? listColumn(column.key);
  const context: ListCellContext = {
    value: rowCellValue(cell, decision),
    cell,
    decision,
  };
  const text = (column.text ?? defaultText(definition))(context);
  const tag = cellTag(cell, decision);
  const waiting = (cell ? fieldNeedsReview(cell) : false) || !!decision;
  const italic = cell?.status === 'proposed' && decision?.kind !== 'type';

  const close = useCallback(
    (returnFocus: boolean) => {
      onOpenChange(false);
      if (returnFocus) buttonRef.current?.focus();
    },
    [onOpenChange]
  );

  if (onActivate) {
    // The name of an owner or an officer opens the person window, so it is drawn as a link: no
    // border, no chevron, nothing that reads as a picker.
    return (
      <div className='inline-flex flex-wrap max-w-full items-center gap-x-2 gap-y-1'>
        <button
          ref={buttonRef}
          type='button'
          aria-haspopup='dialog'
          aria-label={`${column.header}: ${text}${tag ? `, ${tag}` : ''}`}
          data-testid={testId}
          className='link link-primary link-hover text-left max-w-full'
          disabled={!cell}
          onClick={onActivate}
        >
          <span
            className={`${VALUE_TEXT_CLASS} ${italic ? 'italic' : ''} ${
              bold ? 'font-bold' : ''
            }`}
          >
            {column.render ? column.render(context) : text}
          </span>
        </button>
        {tag && (
          <span className='badge badge-warning badge-sm whitespace-nowrap'>
            {tag}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className='relative'>
      <button
        ref={buttonRef}
        type='button'
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        aria-label={`${column.header}: ${text}${tag ? `, ${tag}` : ''}`}
        data-testid={testId}
        className={`${CELL_BUTTON_CLASS} ${
          waiting
            ? 'border-dashed border-warning bg-warning/10'
            : 'border-transparent hover:border-base-300'
        } ${isOpen ? 'bg-base-200' : ''}`}
        disabled={busy || !cell}
        onClick={() => onOpenChange(!isOpen)}
      >
        <span
          className={`${VALUE_TEXT_CLASS} ${italic ? 'italic' : ''} ${
            bold ? 'font-bold' : ''
          }`}
        >
          {column.render ? column.render(context) : text}
        </span>
        {tag && (
          <span className='badge badge-warning badge-sm whitespace-nowrap'>
            {tag}
          </span>
        )}
        <FontAwesomeIcon icon={faChevronDown} className='text-xs opacity-50' />
      </button>
      {isOpen && cell && (
        <ValuePicker
          field={cell}
          definition={definition}
          anchorRef={buttonRef}
          onDecide={(chosen) => {
            close(true);
            onDecide(chosen);
          }}
          onClose={close}
          onOpenSourceDocument={onOpenSourceDocument}
          busy={busy}
          thisDocumentId={thisDocumentId}
          {...(rowWaiting
            ? {
                acceptLabel: ROW_WORDS.useThisValue,
                note,
                initialDecision: decision ?? null,
              }
            : {})}
        />
      )}
    </div>
  );
}

/**
 * The keys of the row's cells the table does not draw, in the order of TAX_PROFILE_LIST_COLUMNS:
 * what the person window shows besides the name.
 */
export const detailKeys = (
  row: TaxProfileListRow,
  columns: ListTableColumn[]
): string[] =>
  TAX_PROFILE_LIST_COLUMNS.map((column) => column.key).filter(
    (key) => key in row.cells && !columns.some((shown) => shown.key === key)
  );

export default function ListTable({
  rows,
  columns,
  label,
  nameKey,
  noun,
  taxYear,
  needsReviewOnly,
  writes,
  busy = false,
  onOpenSourceDocument,
  personDetails = false,
  highlightedElementId = null,
  renderFooter,
  onUnsavedChoicesChange,
}: ListTableProps) {
  /**
   * The choices made in each waiting row's cells, by the row's recordId and then by decisionKey
   * (listRows.ts), so a per-year column's choice stays with its tax year and comes back when the
   * page switches back to that year. The rows of another legal entity keep theirs the same way,
   * since recordIds are not shared between entities.
   */
  const [decisions, setDecisions] = useState<Record<number, KeptRowDecisions>>(
    {}
  );
  /**
   * The row whose Finish review was clicked, which shows Cancel and Confirm review: in the row, or
   * in the person window's footer with personDetails.
   */
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  /** The cells the api refused at a row's last Confirm review, with its sentence, by recordId. */
  const [failures, setFailures] = useState<Record<number, RowWriteFailure[]>>(
    {}
  );
  /** The cell whose ValuePicker is open, as `<recordId>:<key>`, or `details:<recordId>:<key>`. */
  const [openCell, setOpenCell] = useState<string | null>(null);
  /** The row whose person window is open (personDetails); null while none is. */
  const [detailsId, setDetailsId] = useState<number | null>(null);
  /** True once the window's Remove button was clicked: the window asks before anything is sent. */
  const [askingToRemove, setAskingToRemove] = useState(false);
  /** The api's sentence for the window's last removal it refused; null when there is none. */
  const [removeError, setRemoveError] = useState<string | null>(null);

  const decisionsOf = (row: TaxProfileListRow): RowDecisions =>
    decisionsForYear(row, decisions[row.recordId], taxYear);

  /**
   * How many rows have choices not saved yet: every row with a kept choice, in any tax year or on
   * any legal entity, except a row on screen that no longer waits for a person (somebody approved
   * it meanwhile), whose choices have no Confirm review to send them and would warn forever.
   */
  const unsavedCount = Object.entries(decisions).filter(([recordId, kept]) => {
    if (Object.keys(kept).length === 0) return false;
    const row = rows.find((entry) => entry.recordId === Number(recordId));
    return !row || rowIsWaiting(row);
  }).length;

  useEffect(() => {
    onUnsavedChoicesChange?.(noun, unsavedCount);
  }, [onUnsavedChoicesChange, noun, unsavedCount]);

  // Reports 0 once the table goes away (the list became empty on a new entity), since its choices
  // go with it.
  useEffect(
    () => () => onUnsavedChoicesChange?.(noun, 0),
    [onUnsavedChoicesChange, noun]
  );

  const headerOf = (key: string) =>
    columns.find((column) => column.key === key)?.header ??
    listColumn(key).label;

  const shownRows = needsReviewOnly ? rows.filter(rowIsWaiting) : rows;

  const decide = (
    row: TaxProfileListRow,
    column: ListTableColumn,
    decision: FieldDecision
  ) => {
    // A new choice for a cell the api refused replaces what was refused, so the api's sentence
    // under that cell goes with it; it comes back only if the next Confirm review is refused too.
    setFailures((kept) =>
      kept[row.recordId]
        ? {
            ...kept,
            [row.recordId]: kept[row.recordId].filter(
              (failure) => failure.key !== column.key
            ),
          }
        : kept
    );
    if (rowIsWaiting(row)) {
      const key = decisionKey(
        column.key,
        row.cells[column.key]?.taxYear ?? taxYear
      );
      setDecisions((kept) => ({
        ...kept,
        [row.recordId]: { ...kept[row.recordId], [key]: decision },
      }));
      return;
    }
    // A row with nothing waiting has no review to wait for: a typed value is written at once. The
    // panel offers only Current and the box on such a cell, and Current closes it without a choice.
    const cell = row.cells[column.key];
    if (decision.kind !== 'type' || !cell) return;
    writes.editValue(
      listCellTarget(row, listColumn(column.key), cell, taxYear),
      decision.value
    );
  };

  const confirm = async (row: TaxProfileListRow) => {
    const rowDecisions = decisionsOf(row);
    const { decided, typed } = rowReviewWrites(row, rowDecisions, taxYear);
    const refused = await writes.reviewListRow(decided, typed);
    setConfirmingId(null);
    setFailures((kept) => ({ ...kept, [row.recordId]: refused }));
    // The person window closes once the api took every cell; with a refusal it stays open, with
    // the api's sentence under each refused cell.
    if (refused.length === 0) {
      setOpenCell(null);
      setDetailsId((open) => (open === row.recordId ? null : open));
    }
    // The choices sent are dropped; those of the cells the api refused stay, so Finish review
    // offers them again, and so do the choices of a per-year column for another tax year, which
    // this Confirm review did not send.
    const refusedKeys = new Set(refused.map((failure) => failure.key));
    const sentKeys = new Set(
      Object.keys(rowDecisions)
        .filter((key) => !refusedKeys.has(key))
        .map((key) => decisionKey(key, row.cells[key]?.taxYear ?? taxYear))
    );
    setDecisions((kept) => ({
      ...kept,
      [row.recordId]: Object.fromEntries(
        Object.entries(kept[row.recordId] ?? {}).filter(
          ([key]) => !sentKeys.has(key)
        )
      ),
    }));
  };

  /** The notice the page keeps for one cell of the row after a write, if any. */
  const noticeOfCell = (
    row: TaxProfileListRow,
    cell: TaxProfileFieldValue | undefined
  ): FieldRowNotice | null =>
    cell
      ? writes.notices[noticeKey(cell.key, cell.taxYear, row.recordId)] ?? null
      : null;

  /** The notice the page keeps for one of the row's cells after a write, if any. */
  const noticeOf = (row: TaxProfileListRow) =>
    Object.values(row.cells)
      .map((cell) => noticeOfCell(row, cell))
      .find((notice) => notice !== null) ?? null;

  const openDetails = (row: TaxProfileListRow) => {
    setOpenCell(null);
    setConfirmingId(null);
    setAskingToRemove(false);
    setRemoveError(null);
    setDetailsId(row.recordId);
  };

  /**
   * Finish review, or Cancel and a green Confirm review once Finish review was clicked: the row's
   * Approval cell draws them in the subsidiaries and state registrations tables, the person window's
   * footer in the owners and officers tables.
   */
  const reviewButtons = (row: TaxProfileListRow, className: string) =>
    confirmingId === row.recordId ? (
      <span className={`flex flex-wrap gap-2 ${className}`}>
        <button
          type='button'
          className='btn btn-sm btn-ghost'
          onClick={() => setConfirmingId(null)}
        >
          {ROW_WORDS.cancel}
        </button>
        <button
          type='button'
          className='btn btn-sm btn-success'
          disabled={busy}
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            confirm(row);
          }}
        >
          {ROW_WORDS.confirmReview}
        </button>
      </span>
    ) : (
      <button
        type='button'
        className='btn btn-sm btn-warning'
        disabled={busy}
        onClick={() => {
          setOpenCell(null);
          setConfirmingId(row.recordId);
        }}
      >
        {ROW_WORDS.finishReview}
      </button>
    );

  const approvalCell = (row: TaxProfileListRow, waiting: boolean) => {
    // With personDetails the person window draws each refusal under its cell instead.
    const refused = personDetails ? [] : failures[row.recordId] ?? [];
    const refusedLines = refused.map((failure) => {
      const line = `${headerOf(failure.key)}: ${failure.error}`;
      return (
        <span key={line} className='text-xs text-error'>
          {line}
        </span>
      );
    });
    if (waiting && personDetails) {
      // The row is reviewed in the person window, which Finish review opens.
      return (
        <button
          type='button'
          className='btn btn-sm btn-warning'
          disabled={busy}
          onClick={() => openDetails(row)}
        >
          {ROW_WORDS.finishReview}
        </button>
      );
    }
    if (waiting) {
      return (
        <div className='flex flex-col items-end lg:items-center gap-1'>
          {refusedLines}
          {reviewButtons(row, 'justify-end lg:justify-center')}
        </div>
      );
    }
    const notice = noticeOf(row);
    if (notice) {
      return <NoticeText notice={notice} busy={busy} />;
    }
    const approval = rowApproval(row, nameKey);
    return (
      <div className='flex flex-col items-end lg:items-center gap-1'>
        {refusedLines}
        {approval && (
          <span className='inline-flex items-start gap-1 text-xs text-success text-right lg:text-center'>
            <FontAwesomeIcon icon={faCheck} className='mt-0.5' />
            {reviewedSentence(approval)}
          </span>
        )}
      </div>
    );
  };

  /** The cells only the window shows that wait for a person and have no choice yet. */
  const detailsWaitingCount = (
    row: TaxProfileListRow,
    rowDecisions: RowDecisions
  ): number =>
    detailKeys(row, columns).filter(
      (key) => fieldNeedsReview(row.cells[key]) && !rowDecisions[key]
    ).length;

  const detailsRow = personDetails
    ? rows.find((row) => row.recordId === detailsId) ?? null
    : null;

  /**
   * The person window: every cell of the row, each a ListCell as in the row, in two lists, "In the
   * table" and "Details" (the taxpayer number first), and the review buttons or Close.
   */
  const detailsWindow = (row: TaxProfileListRow) => {
    const waiting = rowIsWaiting(row);
    const rowDecisions = decisionsOf(row);
    const refusedByKey = new Map(
      (failures[row.recordId] ?? []).map((failure) => [
        failure.key,
        failure.error,
      ])
    );
    const detailColumns: ListTableColumn[] = detailKeys(row, columns).map(
      (key) => ({
        key,
        header: listColumn(key).yearly
          ? labelForYear(listColumn(key).label, taxYear)
          : listColumn(key).label,
      })
    );
    const heading = personDetailsHeading(noun);
    const name =
      rowCellValue(row.cells[nameKey], rowDecisions[nameKey]) ??
      EMPTY_CELL_DASH;
    const close = () => {
      setOpenCell(null);
      setConfirmingId(null);
      setAskingToRemove(false);
      setRemoveError(null);
      setDetailsId(null);
    };
    const personTable = PERSON_TABLES[noun];
    const { removeListRow } = writes;
    const remove = async () => {
      if (!personTable || !removeListRow) return;
      setRemoveError(null);
      const refusal = await removeListRow(personTable, row.recordId);
      if (refusal !== null) {
        setAskingToRemove(false);
        setRemoveError(refusal);
        return;
      }
      // The row is gone: what was chosen or refused in it goes with it.
      setDecisions(({ [row.recordId]: _decided, ...kept }) => kept);
      setFailures(({ [row.recordId]: _refused, ...kept }) => kept);
      close();
    };
    const removeControls =
      personTable && removeListRow ? (
        <div className='flex flex-col gap-2'>
          {askingToRemove ? (
            <div
              className='flex flex-wrap items-center gap-2'
              data-testid={`tax-profile-details-remove-question-${row.recordId}`}
            >
              <span className='text-error'>
                {removePersonQuestion(String(name))}
              </span>
              <button
                type='button'
                className='btn btn-sm btn-ghost'
                onClick={() => setAskingToRemove(false)}
              >
                {REMOVE_PERSON_WORDS.cancel}
              </button>
              <button
                type='button'
                className='btn btn-sm btn-error'
                disabled={busy}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  remove();
                }}
              >
                {REMOVE_PERSON_WORDS.confirm}
              </button>
            </div>
          ) : (
            <button
              type='button'
              className='btn btn-sm btn-ghost text-error self-start'
              disabled={busy}
              onClick={() => {
                setOpenCell(null);
                setConfirmingId(null);
                setRemoveError(null);
                setAskingToRemove(true);
              }}
            >
              {removePersonButton(noun)}
            </button>
          )}
          {removeError && (
            <p
              className='text-xs text-error'
              data-testid={`tax-profile-details-remove-error-${row.recordId}`}
            >
              {removeError}
            </p>
          )}
        </div>
      ) : null;
    const termClass = 'text-xs font-bold uppercase opacity-60 pt-1';
    const listClass =
      'grid grid-cols-1 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)] gap-x-4 gap-y-2 items-start';
    const cellEntry = (column: ListTableColumn) => {
      const cellId = `details:${row.recordId}:${column.key}`;
      // A waiting row writes nothing until Confirm review, so only a row with nothing waiting has
      // a "Saved" notice to show here.
      const cellNotice = waiting
        ? null
        : noticeOfCell(row, row.cells[column.key]);
      const refusal = refusedByKey.get(column.key);
      return (
        <Fragment key={column.key}>
          <dt className={termClass}>{column.header}</dt>
          <dd className='min-w-0 break-words'>
            <ListCell
              column={column}
              cell={row.cells[column.key]}
              decision={rowDecisions[column.key]}
              rowWaiting={waiting}
              isOpen={openCell === cellId}
              onOpenChange={(open) => setOpenCell(open ? cellId : null)}
              onDecide={(decision) => decide(row, column, decision)}
              busy={busy}
              onOpenSourceDocument={onOpenSourceDocument}
              testId={`tax-profile-details-cell-${row.recordId}-${column.key}`}
            />
            {refusal && (
              <p
                className='mt-1 text-xs text-error'
                data-testid={`tax-profile-details-error-${row.recordId}-${column.key}`}
              >
                {refusal}
              </p>
            )}
            {cellNotice && (
              <div
                className='mt-1'
                data-testid={`tax-profile-details-notice-${row.recordId}-${column.key}`}
              >
                <NoticeText notice={cellNotice} busy={busy} />
              </div>
            )}
          </dd>
        </Fragment>
      );
    };
    return (
      <GenericModal
        isOpen
        onClose={close}
        className='w-[calc(100vw-2rem)] max-w-5xl'
      >
        <div
          role='dialog'
          aria-label={`${heading}: ${String(name)}`}
          data-testid={`tax-profile-details-${row.recordId}`}
          className='flex flex-col gap-4 text-sm'
        >
          <div className='pr-8'>
            <h3 className='font-bold text-lg break-words'>{String(name)}</h3>
            <p className='text-xs opacity-70'>{heading}</p>
          </div>
          <section className='flex flex-col gap-2'>
            <h4 className='font-bold'>{PERSON_DETAILS_WORDS.inTable}</h4>
            <dl className={listClass}>{columns.map(cellEntry)}</dl>
          </section>
          <section className='flex flex-col gap-2'>
            <h4 className='font-bold'>{PERSON_DETAILS_WORDS.details}</h4>
            <dl className={listClass}>{detailColumns.map(cellEntry)}</dl>
          </section>
          <div className='flex flex-wrap items-start justify-between gap-2'>
            {removeControls ?? <span />}
            {!askingToRemove &&
              (waiting ? (
                reviewButtons(row, 'justify-end')
              ) : (
                <button type='button' className='btn btn-sm' onClick={close}>
                  {PERSON_DETAILS_WORDS.close}
                </button>
              ))}
          </div>
        </div>
      </GenericModal>
    );
  };

  if (shownRows.length === 0) {
    return (
      <p className='text-xs opacity-70'>{LIST_EMPTY_SENTENCES.filteredOut}</p>
    );
  }

  return (
    // From lg up, table-fixed and the header widths decide the columns. There is no overflow
    // wrapper, so a cell's open ValuePicker (position absolute) hangs out of the table instead of
    // being cut off.
    <>
      <table className={LIST_TABLE_LAYOUT.table} aria-label={label}>
        <thead className={LIST_TABLE_LAYOUT.head}>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`${LIST_TABLE_LAYOUT.header} ${
                  column.widthClass ?? ''
                } ${column.align === 'right' ? 'text-right' : ''}`}
              >
                {column.header}
              </th>
            ))}
            <th className='w-[20%] lg:text-center' aria-label='Approval' />
          </tr>
        </thead>
        <tbody className={LIST_TABLE_LAYOUT.body}>
          {shownRows.map((row) => {
            const waiting = rowIsWaiting(row);
            const rowDecisions = decisionsOf(row);
            const nameCell = row.cells[nameKey];
            const edge = waiting ? 'border-l-warning' : 'border-l-transparent';
            const elementId = listRowElementId(noun, row.recordId);
            const highlight =
              elementId === highlightedElementId ? HIGHLIGHT_ROW_CLASS : '';
            return (
              <tr
                key={row.recordId}
                id={elementId}
                data-testid={`tax-profile-row-${row.recordId}`}
                data-highlighted={highlight ? true : undefined}
                className={`${LIST_TABLE_LAYOUT.row} ${edge} scroll-mt-4 ${highlight}`}
              >
                {columns.map((column, index) => {
                  const cellId = `${row.recordId}:${column.key}`;
                  const isName = index === 0;
                  return (
                    <td
                      key={column.key}
                      data-label={isName ? undefined : column.header}
                      className={`align-top whitespace-normal break-words ${
                        column.align === 'right' ? 'lg:text-right' : ''
                      } ${
                        isName
                          ? `${LIST_TABLE_LAYOUT.nameCell} ${edge}`
                          : LIST_TABLE_LAYOUT.cell
                      }`}
                    >
                      <ListCell
                        column={column}
                        testId={`tax-profile-cell-${row.recordId}-${column.key}`}
                        cell={row.cells[column.key]}
                        decision={rowDecisions[column.key]}
                        rowWaiting={waiting}
                        isOpen={openCell === cellId}
                        onOpenChange={(open) =>
                          setOpenCell(open ? cellId : null)
                        }
                        onDecide={(decision) => decide(row, column, decision)}
                        busy={busy}
                        onOpenSourceDocument={onOpenSourceDocument}
                        bold={isName}
                        onActivate={
                          isName && personDetails
                            ? () => openDetails(row)
                            : undefined
                        }
                      />
                      {isName && row.isNew && (
                        <SourceText
                          pieces={newRowLine(
                            noun,
                            (nameCell && newestWaiting(nameCell)?.sources[0]) ??
                              null,
                            nameKey
                          )}
                          onOpenDocument={onOpenSourceDocument}
                          className='block text-xs text-warning mt-1'
                        />
                      )}
                      {isName && personDetails && (
                        <>
                          <RefusedLine
                            show={
                              detailsId !== row.recordId &&
                              (failures[row.recordId] ?? []).length > 0
                            }
                            onOpen={() => openDetails(row)}
                          />
                          <DetailsWaitingLine
                            count={detailsWaitingCount(row, rowDecisions)}
                            onOpen={() => openDetails(row)}
                          />
                        </>
                      )}
                    </td>
                  );
                })}
                <td
                  className={`align-top whitespace-normal ${LIST_TABLE_LAYOUT.approvalCell}`}
                >
                  {approvalCell(row, waiting)}
                </td>
              </tr>
            );
          })}
        </tbody>
        {renderFooter && (
          <tfoot className={LIST_TABLE_LAYOUT.foot}>
            {renderFooter(decisionsOf)}
          </tfoot>
        )}
      </table>
      {detailsRow && detailsWindow(detailsRow)}
    </>
  );
}
